/** GP / LP / Startup / ServiceProvider sidebar — ui-prototype-all */

import { canAccessFeature } from '../../constants/featureAccess'

export const PORTAL_ROLES = ['GP', 'LP', 'Startup', 'ServiceProvider']

const SHARED_TOOLS = [
  { divider: true },
  { path: '/chat', label: 'Chat', unreadKey: 'chat', featureKey: 'chat' },
  { path: '/email-campaigns', label: 'Email', featureKey: 'email' },
  { path: '/documents', label: 'Files', featureKey: 'files' },
  { path: '/digest', label: 'Digest', unreadKey: 'digest', featureKey: 'digest' },
]

export const GP_NAV = [
  { path: '/dashboard', label: 'Home', featureKey: 'home' },
  { path: '/deals?action=create', label: 'Post a Deal', lockBadge: true, featureKey: 'post_deal' },
  { path: '/deals?tab=browse', label: 'Find a Deal', featureKey: 'find_deal' },
  { path: '/deals', label: 'In Progress', matchPath: '/deals', excludeQuery: ['tab', 'action'], featureKey: 'in_progress' },
  ...SHARED_TOOLS,
  { divider: true },
  { path: '/payment', label: 'Upgrade Plan', featureKey: 'payment' },
  { path: '/settings', label: 'Settings', featureKey: 'settings' },
]

export const LP_NAV = [
  { path: '/dashboard', label: 'Home', featureKey: 'home' },
  { path: '/deals?tab=browse', label: 'Find a Deal', featureKey: 'find_deal' },
  { path: '/assets', label: 'Sell My Portfolio', lockBadge: true, featureKey: 'assets' },
  { path: '/deals', label: 'In Progress', matchPath: '/deals', excludeQuery: ['tab'], featureKey: 'in_progress' },
  ...SHARED_TOOLS,
  { divider: true },
  { path: '/payment', label: 'Upgrade Plan', featureKey: 'payment' },
  { path: '/settings', label: 'Settings', featureKey: 'settings' },
]

/** startup-dashboard.html */
export const STARTUP_NAV = [
  { path: '/dashboard', label: 'Home', featureKey: 'home' },
  { path: '/deals?action=create', label: 'Funding', lockBadge: true, featureKey: 'post_deal' },
  { path: '/deals', label: 'In Progress', matchPath: '/deals', excludeQuery: ['tab', 'action'], featureKey: 'in_progress' },
  ...SHARED_TOOLS,
  { divider: true },
  { path: '/payment', label: 'Upgrade Plan', featureKey: 'payment' },
  { path: '/settings', label: 'Settings', featureKey: 'settings' },
]

/** third-party-dashboard.html / other-dashboard.html */
export const SERVICE_PROVIDER_NAV = [
  { path: '/dashboard', label: 'Home', featureKey: 'home' },
  { path: '/deals', label: 'In Progress', matchPath: '/deals', excludeQuery: ['tab', 'action'], featureKey: 'in_progress' },
  ...SHARED_TOOLS,
  { path: '/settings', label: 'Settings', featureKey: 'settings' },
]

export function getPortalNav(role) {
  if (role === 'GP') return GP_NAV
  if (role === 'LP') return LP_NAV
  if (role === 'Startup') return STARTUP_NAV
  if (role === 'ServiceProvider') return SERVICE_PROVIDER_NAV
  return null
}

export function preparePortalNav(nav) {
  if (!nav) return null
  const cleaned = []
  for (const item of nav) {
    if (item.divider && (!cleaned.length || cleaned[cleaned.length - 1]?.divider)) continue
    cleaned.push(item)
  }
  if (cleaned[cleaned.length - 1]?.divider) cleaned.pop()
  return cleaned
}

/** @deprecated use preparePortalNav — items stay visible when admin disables a feature */
export function filterPortalNav(nav, featureAccess) {
  return preparePortalNav(nav)
}

export function isNavItemLocked(item, user) {
  if (!item?.featureKey || !user) return null
  if (user.feature_access?.[item.featureKey] === false) return 'admin'
  if (item.lockBadge && !user.is_paid) return 'paywall'
  return null
}

const PATH_FEATURE_FALLBACK = {
  '/dashboard': 'home',
  '/chat': 'chat',
  '/documents': 'files',
  '/digest': 'digest',
  '/email-campaigns': 'email',
  '/settings': 'settings',
  '/payment': 'payment',
  '/assets': 'assets',
}

export function resolveActivePortalNavItem(pathname, searchParams, role) {
  const nav = getPortalNav(role)
  if (!nav) return null
  const sp = searchParams instanceof URLSearchParams
    ? searchParams
    : new URLSearchParams(searchParams || '')

  const matches = nav.filter((item) => !item.divider && isPortalNavActive(item, pathname, sp))
  if (matches.length) {
    return matches.sort((a, b) => {
      const score = (item) => {
        if (item.path.includes('?')) return 3
        if (item.matchPath) return 2
        return 1
      }
      return score(b) - score(a)
    })[0]
  }

  const fallbackKey = PATH_FEATURE_FALLBACK[pathname]
  if (fallbackKey) {
    return nav.find((item) => item.featureKey === fallbackKey) || { featureKey: fallbackKey }
  }
  return null
}

export function resolvePortalFeatureLock(pathname, searchParams, role, user) {
  if (!user || !PORTAL_ROLES.includes(role)) {
    return { locked: false, lockType: null, featureKey: null, label: null }
  }

  if (pathname === '/payment') {
    return { locked: false, lockType: null, featureKey: 'payment', label: 'Upgrade Plan' }
  }

  const navItem = resolveActivePortalNavItem(pathname, searchParams, role)
  const featureKey = navItem?.featureKey || PATH_FEATURE_FALLBACK[pathname] || null
  if (!featureKey) {
    return { locked: false, lockType: null, featureKey: null, label: navItem?.label || null }
  }

  const adminLocked = !canAccessFeature(user.feature_access, featureKey)
  const paywallLocked = Boolean(navItem?.lockBadge && !user.is_paid)

  if (adminLocked) {
    return { locked: true, lockType: 'admin', featureKey, label: navItem?.label || null }
  }
  if (paywallLocked) {
    return { locked: true, lockType: 'paywall', featureKey, label: navItem?.label || null }
  }
  return { locked: false, lockType: null, featureKey, label: navItem?.label || null }
}

export function portalSubtitle(role) {
  if (role === 'GP') return 'GP Portal'
  if (role === 'LP') return 'LP Portal'
  if (role === 'Startup') return 'Startup Portal'
  if (role === 'ServiceProvider') return 'Service Provider'
  return null
}

/** Active state for nav items with query params */
export function isPortalNavActive(item, pathname, searchParams) {
  if (item.divider) return false

  const itemUrl = new URL(item.path, 'http://local')
  const itemPath = itemUrl.pathname
  const itemQuery = itemUrl.searchParams

  if (item.matchPath) {
    if (pathname !== item.matchPath) return false
    if (item.excludeQuery?.length) {
      for (const key of item.excludeQuery) {
        if (searchParams.get(key)) return false
      }
    }
    return true
  }

  if (pathname !== itemPath) return false

  if (itemQuery.toString()) {
    for (const [k, v] of itemQuery.entries()) {
      if (searchParams.get(k) !== v) return false
    }
    return true
  }

  if (itemPath === '/deals' && (searchParams.get('tab') || searchParams.get('action'))) {
    return false
  }

  return pathname === itemPath
}
